/**
 * Utilitários para busca de CEP e cálculo de frete usando coordenadas geográficas
 */

export interface CEPData {
  cep: string;
  logradouro: string;
  complemento: string;
  bairro: string;
  localidade: string;
  uf: string;
  erro?: boolean;
}

export interface Coordenadas {
  lat: number;
  lng: number;
}

// Cache de coordenadas para evitar múltiplas chamadas à API
const coordenadasCache = new Map<string, Coordenadas>();

/**
 * Buscar dados do CEP via API ViaCEP
 */
export async function buscarCEP(cep: string): Promise<CEPData | null> {
  try {
    // Remover caracteres não numéricos
    const cepLimpo = cep.replace(/\D/g, '');
    
    if (cepLimpo.length !== 8) {
      return null;
    }

    const response = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
    
    if (!response.ok) {
      return null;
    }

    const data: CEPData = await response.json();
    
    if (data.erro) {
      return null;
    }

    return data;
  } catch (error) {
    console.error('Erro ao buscar CEP:', error);
    return null;
  }
}

/**
 * Buscar coordenadas geográficas (lat/lng) de um CEP
 * Usa OpenStreetMap Nominatim API (gratuita, sem chave)
 */
export async function buscarCoordenadasCEP(cep: string): Promise<Coordenadas | null> {
  try {
    // Verificar cache primeiro
    const cepLimpo = cep.replace(/\D/g, '');
    if (coordenadasCache.has(cepLimpo)) {
      return coordenadasCache.get(cepLimpo) || null;
    }

    // Buscar dados do CEP primeiro
    const dadosCEP = await buscarCEP(cep);
    if (!dadosCEP) {
      return null;
    }

    // Montar query de busca para geocoding
    // Tentar buscar com endereço completo primeiro
    const query = `${dadosCEP.logradouro}, ${dadosCEP.bairro}, ${dadosCEP.localidade}, ${dadosCEP.uf}, Brasil, ${dadosCEP.cep}`;
    
    // Usar Nominatim (OpenStreetMap) para geocoding
    // Adicionar delay para respeitar rate limit (1 requisição por segundo)
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const nominatimUrl = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1&countrycodes=br`;
    
    const response = await fetch(nominatimUrl, {
      headers: {
        'User-Agent': 'BradGrifes-Ecommerce/1.0', // Nominatim requer User-Agent
      },
    });

    if (!response.ok) {
      // Fallback: tentar apenas com cidade e estado
      const queryFallback = `${dadosCEP.localidade}, ${dadosCEP.uf}, Brasil`;
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const responseFallback = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(queryFallback)}&limit=1&countrycodes=br`,
        {
          headers: {
            'User-Agent': 'BradGrifes-Ecommerce/1.0',
          },
        }
      );

      if (!responseFallback.ok) {
        return null;
      }

      const dataFallback = await responseFallback.json();
      if (dataFallback.length === 0) {
        return null;
      }

      const coordenadas: Coordenadas = {
        lat: parseFloat(dataFallback[0].lat),
        lng: parseFloat(dataFallback[0].lon),
      };

      // Cachear coordenadas
      coordenadasCache.set(cepLimpo, coordenadas);
      return coordenadas;
    }

    const data = await response.json();
    if (data.length === 0) {
      return null;
    }

    const coordenadas: Coordenadas = {
      lat: parseFloat(data[0].lat),
      lng: parseFloat(data[0].lon),
    };

    // Cachear coordenadas
    coordenadasCache.set(cepLimpo, coordenadas);
    return coordenadas;
  } catch (error) {
    console.error('Erro ao buscar coordenadas do CEP:', error);
    return null;
  }
}

/**
 * Calcular distância entre duas coordenadas usando a fórmula de Haversine
 * Retorna a distância em quilômetros
 */
export function calcularDistanciaCoordenadas(coord1: Coordenadas, coord2: Coordenadas): number {
  const R = 6371; // Raio da Terra em km
  const dLat = (coord2.lat - coord1.lat) * Math.PI / 180;
  const dLon = (coord2.lng - coord1.lng) * Math.PI / 180;
  
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(coord1.lat * Math.PI / 180) * Math.cos(coord2.lat * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distancia = R * c;
  
  return distancia;
}

/**
 * Calcular distância entre dois CEPs usando coordenadas geográficas
 * Usa geocoding para obter coordenadas e fórmula de Haversine para calcular distância
 */
export async function calcularDistanciaCEP(cepOrigem: string, cepDestino: string): Promise<number> {
  try {
    // Remover caracteres não numéricos
    const origem = cepOrigem.replace(/\D/g, '');
    const destino = cepDestino.replace(/\D/g, '');
    
    if (origem.length !== 8 || destino.length !== 8) {
      return 0;
    }

    // Se for o mesmo CEP, distância é 0
    if (origem === destino) {
      return 0;
    }

    // Buscar coordenadas de ambos os CEPs
    const [coordOrigem, coordDestino] = await Promise.all([
      buscarCoordenadasCEP(cepOrigem),
      buscarCoordenadasCEP(cepDestino),
    ]);

    // Se não conseguir obter coordenadas, retornar 0
    if (!coordOrigem || !coordDestino) {
      console.warn('Não foi possível obter coordenadas para um ou ambos os CEPs');
      return 0;
    }

    // Calcular distância usando fórmula de Haversine
    const distancia = calcularDistanciaCoordenadas(coordOrigem, coordDestino);
    
    // Mínimo de 1.5km (distância mínima para cálculo de frete)
    return Math.max(1.5, distancia);
  } catch (error) {
    console.error('Erro ao calcular distância entre CEPs:', error);
    return 0;
  }
}

/**
 * Calcular frete baseado na distância
 * Valor mínimo: R$ 7,00 para 1,5km
 * Aumenta exponencialmente conforme a distância
 */
export function calcularFrete(distanciaKm: number): number {
  // Distância mínima de 1.5km = R$ 7,00
  if (distanciaKm <= 1.5) {
    return 7.0;
  }

  // Calcular acréscimo exponencial
  // Fórmula: 7 + (distância - 1.5) ^ 1.5 * 0.5
  const distanciaExtra = distanciaKm - 1.5;
  const acrescimo = Math.pow(distanciaExtra, 1.5) * 0.5;
  
  // Arredondar para 2 casas decimais
  const frete = 7.0 + acrescimo;
  
  return Math.round(frete * 100) / 100;
}

/**
 * Format CEP
 */
export function formatCEP(cep: string): string {
  const cepLimpo = cep.replace(/\D/g, '');
  
  if (cepLimpo.length <= 5) {
    return cepLimpo;
  }
  
  return `${cepLimpo.slice(0, 5)}-${cepLimpo.slice(5, 8)}`;
}

